import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import type Handsontable from 'handsontable-v16';
import { TextEditor } from 'handsontable-v16/editors';
import { registerCellType } from 'handsontable-v16/cellTypes';
import type { CellProperties } from 'handsontable-v16/settings';
import { _notNil } from '@/littledash';
import { parseScoringScale } from '@/utils/observationTypeValidation';
import { ScoringScaleModal, isValidScoringScaleValue } from '../editors/ScoringScaleModal';
import styles from '../editors/ScoringScaleEditor.module.scss';

export const scoringScaleRendererV16 = (
  hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  _prop: string | number,
  value: unknown,
  cellProperties: CellProperties
): HTMLTableCellElement => {
  td.innerHTML = '';
  td.style.padding = '0';
  td.style.verticalAlign = 'middle';

  td.classList.remove('htInvalid');

  const cellMeta = hotInstance.getCellMeta(row, col);
  if (cellMeta.valid === false && _notNil(value) && value !== '') {
    td.classList.add('htInvalid');
  }

  const isReadOnly = cellProperties.readOnly === true;

  const wrapper = document.createElement('div');
  wrapper.className = isReadOnly ? `${styles.scoringScaleCell} ${styles.readOnly}` : styles.scoringScaleCell;

  const scaleValueDiv = document.createElement('div');
  scaleValueDiv.className = styles.scaleValue;

  if (!_notNil(value) || value === '') {
    if (!isReadOnly) {
      const placeholder = document.createElement('span');
      placeholder.className = styles.placeholder;
      placeholder.textContent = 'Click to set scale';
      scaleValueDiv.appendChild(placeholder);
    }
  } else {
    const badge = document.createElement('div');
    badge.className = styles.scaleBadge;

    const badgeText = document.createElement('span');
    badgeText.className = styles.badgeText;
    badgeText.textContent = String(value);
    badge.appendChild(badgeText);

    if (!isReadOnly) {
      const clearButton = document.createElement('button');
      clearButton.className = styles.badgeClearButton;
      clearButton.innerHTML = '×';
      clearButton.setAttribute('aria-label', 'Clear scale');
      clearButton.onclick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        hotInstance.setDataAtCell(row, col, '');
      };
      badge.appendChild(clearButton);
    }

    scaleValueDiv.appendChild(badge);
  }

  wrapper.appendChild(scaleValueDiv);
  td.appendChild(wrapper);

  return td;
};

export const scoringScaleValidatorV16 = (value: unknown, callback: (valid: boolean) => void): void => {
  callback(isValidScoringScaleValue(value));
};

export class ScoringScaleEditorV16 extends TextEditor {
  static type = 'scoring-scale-v16';

  private _modalContainer: HTMLDivElement | null = null;
  private _modalOverlay: HTMLDivElement | null = null;
  private _root: ReactDOM.Root | null = null;
  private _currentValue = '';
  private _initialValue?: unknown;
  private _isOpened = false;
  private _isDestroyed = false;

  static register(cellType = ScoringScaleEditorV16.type) {
    registerCellType(cellType, {
      editor: ScoringScaleEditorV16 as typeof TextEditor,
      renderer: scoringScaleRendererV16,
      validator: scoringScaleValidatorV16,
    });
  }

  init() {
    super.init();
    this.hot.addHook('afterDestroy', () => this._destroyModal());
  }

  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: CellProperties
  ) {
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.originalValue = originalValue;
    this.cellProperties = cellProperties;
    this._initialValue = originalValue;

    this._cleanupModal();
  }

  focus(): void {
    // Modal handles focus
  }

  beginEditing(): void {
    this.open();
  }

  open(): void {
    if (this._isDestroyed) {
      return;
    }

    this._isOpened = true;
    this._currentValue = (this._initialValue as string) ?? '';
    const scale = parseScoringScale(this._currentValue);

    this._modalContainer = this.hot.rootDocument.createElement('div');
    this._modalContainer.classList.add('scoring-scale-editor-container');
    this.hot.rootDocument.body.appendChild(this._modalContainer);

    this._modalOverlay = this.hot.rootDocument.createElement('div');
    this._modalOverlay.classList.add('scoring-scale-editor-overlay');
    this._modalContainer.appendChild(this._modalOverlay);

    this._root = ReactDOM.createRoot(this._modalOverlay);

    const handleChange = (value: string): void => {
      if (this._isDestroyed) {
        return;
      }
      this._currentValue = value;
      this.hot.setDataAtCell(this.row, this.col, value);
      this.close();
    };

    const handleClose = (): void => {
      if (this._isDestroyed) {
        return;
      }
      this.close();
    };

    this._root.render(
      <StrictMode>
        <ScoringScaleModal defaultValue={scale} td={this.TD} onChange={handleChange} onClose={handleClose} />
      </StrictMode>
    );
  }

  close(): void {
    if (!this._isOpened) {
      return;
    }
    this._isOpened = false;
    this._cleanupModal();

    if (!this._isDestroyed) {
      this.hot.render();
    }
  }

  getValue(): string {
    return this._currentValue;
  }

  setValue(newValue: string): void {
    this._currentValue = newValue ?? '';
  }

  isOpened(): boolean {
    return this._isOpened;
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false): void {
    if (this._isDestroyed) {
      return;
    }

    if (!this._isOpened) {
      try {
        super.finishEditing(restoreOriginalValue, ctrlDown);
      } catch {
        // Instance may have been destroyed
      }
      return;
    }

    this.close();

    if (this._isDestroyed) {
      return;
    }

    try {
      if (restoreOriginalValue) {
        const valueToRestore = this._initialValue ?? this.originalValue;
        if (_notNil(valueToRestore)) {
          this.hot.setDataAtCell(this.row, this.col, valueToRestore);
        }
      }
    } catch {
      // Instance may have been destroyed during the operation
    }

    if (this._isDestroyed) {
      return;
    }

    try {
      super.finishEditing(restoreOriginalValue, ctrlDown);
    } catch {
      // Instance may have been destroyed
    }
  }

  private _cleanupModal(): void {
    if (_notNil(this._root)) {
      this._root.unmount();
      this._root = null;
    }

    if (_notNil(this._modalContainer) && _notNil(this._modalContainer.parentNode)) {
      this._modalContainer.parentNode.removeChild(this._modalContainer);
    }
    this._modalContainer = null;
    this._modalOverlay = null;
  }

  private _destroyModal(): void {
    this._isDestroyed = true;
    this._cleanupModal();
  }
}
